// admin/pages/Dashboard.tsx — admin home page. Pure read from Redux.
//
// Every value here moves in real time as the server broadcasts events: a new
// user signup bumps usersTotal + freeTurnsRemaining, a turn consume drops
// the appropriate balance tile, etc. The component does NO fetching — the
// dashboard slice is overwritten on every `dashboard_refresh` event.

import { useMemo } from "react";
import { styles, tokens } from "../styles";
import { useAppSelector } from "../store";
import type { AdminEvent, AdminScenario } from "../store/types";

function formatUsd(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatUsdFromCents(cents: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function formatUsd2dp(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatInt(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export default function Dashboard() {
  const data = useAppSelector((s) => s.dashboard.value);
  const scenarios = useAppSelector((s) => s.scenarios.list);
  const recentEvents = useAppSelector((s) => s.events.list.slice(0, 10));

  const scenarioName = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of scenarios) map.set(s.id, s.name);
    return (id: string) => map.get(id) ?? id;
  }, [scenarios]);

  return (
    <div>
      <h2 style={styles.pageTitle}>Dashboard</h2>
      <p style={styles.pageSub}>
        Live overview. Counters update in real time as users sign up, consume
        turns, and pay.
      </p>

      {!data ? (
        <div style={styles.spinner}>Waiting for sync…</div>
      ) : (
        <>
          {/* Top tiles row — revenue + headline counts */}
          <div style={styles.tileGrid}>
            <Tile
              label="ARR"
              value={formatUsd(data.arrUsd)}
              sub={`from ${formatInt(Math.round(data.mrrUsd / 7.99))} active subscriptions`}
            />
            <Tile
              label="MRR"
              value={`${formatUsd(data.mrrUsd)}/mo`}
              sub="recurring subscription revenue"
            />
            <Tile
              label="Revenue 30d"
              value={formatUsdFromCents(data.revenueLast30dCents)}
              sub="subs + topups, last 30 days"
            />
            <Tile
              label="Users"
              value={formatInt(data.usersTotal)}
              sub={`+${formatInt(data.usersNewLast30d)} new this month`}
            />
          </div>

          {/* Cost-side row — half-width tile so it doesn't compete with the
              revenue numbers above. Sits next to the quiet Ideas (30d)
              tile that flags un-triaged backlog when ideasNewCount > 0. */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 16,
              marginBottom: 24,
            }}
          >
            <CostTile
              label="API spend (30d)"
              value={formatUsd2dp(data.apiSpendLast30dUsd)}
              sub="Anthropic · Claude Sonnet 4.5"
            />
            <IdeasTile
              count30d={data.ideasLast30d}
              newCount={data.ideasNewCount}
            />
          </div>

          {/* Live liability + flow row — new in the socket rework */}
          <div style={{ ...styles.tileGrid, marginTop: 0 }}>
            <Tile
              label="Free turns remaining"
              value={formatInt(data.freeTurnsRemaining)}
              sub="across all free-tier users"
            />
            <Tile
              label="Paid turns outstanding"
              value={formatInt(data.paidTurnsOutstanding)}
              sub="standard + premium balances"
            />
            <Tile
              label="Purchases 30d"
              value={formatInt(data.purchasesLast30d)}
              sub={`${formatUsdFromCents(data.purchasesLast30dCents)} billed`}
            />
            <Tile
              label="Purchases lifetime"
              value={formatInt(data.purchasesTotal)}
              sub={`${formatUsdFromCents(data.purchasesLifetimeCents)} all-time`}
            />
          </div>

          {/* Three panel row + activity feed */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 16,
            }}
          >
            <StoryPopularityPanel
              rows={data.storiesByScenario}
              scenarioName={scenarioName}
            />
            <TurnsPanel
              total={data.turnsLast30d}
              byScenario={data.turnsLast30dByScenario}
              scenarioName={scenarioName}
            />
            <StoryStatePanel
              active={data.storiesActive}
              abandoned={data.storiesAbandoned}
              finished={data.storiesFinished}
              resolutions={data.resolutions}
            />
            <ActivityPanel events={recentEvents} scenarios={scenarios} />
          </div>

          <div
            style={{
              ...styles.panel,
              marginTop: 16,
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
              gap: 16,
            }}
          >
            <TierMini label="Free" value={data.usersByTier.free} />
            <TierMini label="Standard" value={data.usersByTier.standard} />
            <TierMini label="Premium" value={data.usersByTier.premium} />
          </div>
        </>
      )}
    </div>
  );
}

function Tile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div style={styles.tile}>
      <p style={styles.tileLabel}>{label}</p>
      <p style={styles.tileValue}>{value}</p>
      {sub ? <p style={styles.tileSub}>{sub}</p> : null}
    </div>
  );
}

// Lower-prominence variant of <Tile>. Used for cost-side metrics so they
// don't visually compete with customer-facing revenue numbers. Same tile
// chrome (background + border), but tighter padding, smaller value type,
// muted ink. Subtitle uses an even smaller line.
function CostTile({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div style={{ ...styles.tile, padding: 16 }}>
      <p style={styles.tileLabel}>{label}</p>
      <p
        style={{
          ...styles.tileValue,
          fontSize: 20,
          margin: "6px 0 2px",
          color: tokens.inkMuted,
        }}
      >
        {value}
      </p>
      {sub ? (
        <p style={{ ...styles.tileSub, fontSize: 11, color: tokens.inkDim }}>
          {sub}
        </p>
      ) : null}
    </div>
  );
}

// Ideas (30d) — quiet signal tile that lives in the cost-side row. Same
// chrome as CostTile (lower-prominence). Sub-line goes coral when there's
// an un-triaged backlog (ideasNewCount > 0) so an operator catches the
// signal in peripheral vision; otherwise stays muted.
function IdeasTile({
  count30d,
  newCount,
}: {
  count30d: number;
  newCount: number;
}) {
  const hasBacklog = newCount > 0;
  return (
    <div style={{ ...styles.tile, padding: 16 }}>
      <p style={styles.tileLabel}>Ideas (30d)</p>
      <p
        style={{
          ...styles.tileValue,
          fontSize: 20,
          margin: "6px 0 2px",
          color: tokens.inkMuted,
        }}
      >
        {formatInt(count30d)}
      </p>
      <p
        style={{
          ...styles.tileSub,
          fontSize: 11,
          color: hasBacklog ? tokens.destructive : tokens.inkDim,
        }}
      >
        · {formatInt(newCount)} new
      </p>
    </div>
  );
}

function StoryPopularityPanel({
  rows,
  scenarioName,
}: {
  rows: Array<{ scenarioId: string; count: number }>;
  scenarioName: (id: string) => string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div style={styles.panel}>
      <h3 style={styles.panelTitle}>Story popularity</h3>
      {rows.length === 0 ? (
        <div style={styles.empty}>No stories yet.</div>
      ) : (
        rows.map((r, i) => (
          <div key={r.scenarioId} style={i === 0 ? styles.panelRowFirst : styles.panelRow}>
            <span style={{ minWidth: 140 }}>{scenarioName(r.scenarioId)}</span>
            <div style={styles.bar}>
              <div
                style={{
                  ...styles.barFill,
                  width: `${(r.count / max) * 100}%`,
                }}
              />
            </div>
            <span style={{ ...styles.tdMono, color: tokens.inkPrimary, minWidth: 50, textAlign: "right" }}>
              {r.count}
            </span>
          </div>
        ))
      )}
    </div>
  );
}

function TurnsPanel({
  total,
  byScenario,
  scenarioName,
}: {
  total: number;
  byScenario: Array<{ scenarioId: string; count: number }>;
  scenarioName: (id: string) => string;
}) {
  return (
    <div style={styles.panel}>
      <h3 style={styles.panelTitle}>Turns last 30 days</h3>
      <p style={{ ...styles.tileValue, fontSize: 24, margin: "0 0 12px" }}>
        {new Intl.NumberFormat("en-US").format(total)}
      </p>
      {byScenario.length === 0 ? (
        <div style={styles.empty}>No attributable turns yet.</div>
      ) : (
        byScenario.map((r, i) => (
          <div key={r.scenarioId} style={i === 0 ? styles.panelRowFirst : styles.panelRow}>
            <span style={{ flex: 1 }}>{scenarioName(r.scenarioId)}</span>
            <span style={{ ...styles.tdMono, color: tokens.inkPrimary }}>{r.count}</span>
          </div>
        ))
      )}
    </div>
  );
}

function StoryStatePanel({
  active,
  abandoned,
  finished,
  resolutions,
}: {
  active: number;
  abandoned: number;
  finished: number;
  resolutions: number;
}) {
  return (
    <div style={styles.panel}>
      <h3 style={styles.panelTitle}>Story state</h3>
      <div style={{ display: "flex", gap: 16, marginBottom: 12 }}>
        <MiniStat label="Active" value={active} />
        <MiniStat label="Abandoned" value={abandoned} />
        <MiniStat label="Finished" value={finished} />
      </div>
      <div style={{ ...styles.panelRow, borderTop: `1px solid ${tokens.border}` }}>
        <span style={{ flex: 1, color: tokens.inkMuted }}>Resolutions (gameOver = win)</span>
        <span style={{ ...styles.tdMono, color: tokens.inkPrimary }}>{resolutions}</span>
      </div>
    </div>
  );
}

function MiniStat({ label, value }: { label: string; value: number }) {
  return (
    <div style={{ flex: 1 }}>
      <p style={styles.tileLabel}>{label}</p>
      <p style={{ ...styles.tileValue, fontSize: 22, margin: "6px 0 0" }}>
        {new Intl.NumberFormat("en-US").format(value)}
      </p>
    </div>
  );
}

function TierMini({ label, value }: { label: string; value: number }) {
  return (
    <div>
      <p style={styles.tileLabel}>{label}</p>
      <p style={{ ...styles.tileValue, fontSize: 22, margin: "6px 0 0" }}>
        {new Intl.NumberFormat("en-US").format(value)}
      </p>
    </div>
  );
}

function ActivityPanel({
  events,
  scenarios,
}: {
  events: AdminEvent[];
  scenarios: AdminScenario[];
}) {
  const scenarioName = (id: string) =>
    scenarios.find((s) => s.id === id)?.name ?? id;

  return (
    <div style={styles.panel}>
      <h3 style={styles.panelTitle}>Recent activity</h3>
      {events.length === 0 ? (
        <div style={styles.empty}>No events yet.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {events.map((evt, i) => (
            <div
              key={`${evt.at}-${i}`}
              style={{
                display: "flex",
                gap: 10,
                fontSize: 12,
                color: tokens.inkMuted,
              }}
            >
              <span
                style={{
                  fontFamily: tokens.fontMono,
                  color: tokens.inkDim,
                  minWidth: 64,
                }}
              >
                {new Date(evt.at).toLocaleTimeString()}
              </span>
              <span style={{ color: tokens.inkPrimary }}>
                {formatEvent(evt, scenarioName)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatEvent(
  evt: AdminEvent,
  scenarioName: (id: string) => string,
): string {
  switch (evt.type) {
    case "user_signed_up":
      return `New user: ${evt.payload.email}`;
    case "turn_consumed":
      return `Turn consumed (${scenarioName(evt.payload.scenarioId)}, balance ${evt.payload.newBalance})`;
    case "turn_granted": {
      const dollars =
        typeof evt.payload.amountCents === "number"
          ? ` ($${(evt.payload.amountCents / 100).toFixed(2)})`
          : "";
      return `+${evt.payload.delta} turns granted: ${evt.payload.kind}${dollars}`;
    }
    case "subscription_changed":
      return `Subscription → ${evt.payload.status} (${evt.payload.tier})`;
    case "story_created":
      return `Story created: ${evt.payload.name} (${scenarioName(evt.payload.scenarioId)})`;
    case "story_deleted":
      return `Story deleted`;
    case "story_restarted":
      return `Story restarted`;
    case "flag_toggled":
      return `Flag ${evt.payload.flag} → ${evt.payload.value ? "ON" : "OFF"}`;
    case "credit_exhausted":
      return `Anthropic credit exhausted`;
    case "story_idea_submitted":
      return `Idea: ${evt.payload.userEmail}`;
    case "sfx_tag_emitted":
      return `SFX: ${evt.payload.tag} (${evt.payload.category})`;
    case "referral_bonus_deposited":
      return `Referral ${evt.payload.kind} bonus: +${evt.payload.turnsDeposited} turns`;
    case "referral_flag_changed":
      return evt.payload.flagged
        ? `Referral flagged (${evt.payload.reason ?? "unknown"})`
        : `Referral flag cleared`;
    case "account_deletion_scheduled":
      return `Account deletion scheduled`;
    case "account_deletion_cancelled":
      return `Account deletion cancelled`;
    case "account_deletion_finalized":
      return evt.payload.forced
        ? `Account deletion forced by admin`
        : `Account deletion finalized`;
    case "dashboard_refresh":
      // Filtered out before reaching here, but the exhaustiveness check needs it.
      return `Dashboard refresh`;
  }
}
